import { databases, storage } from "@/lib/appwrite";
import { Query } from "appwrite";

const databaseId = import.meta.env.VITE_APPWRITE_DATABASE_ID!;
const collectionId = import.meta.env.VITE_WORKER_PROFILE_COLLECTION_ID!;
const avatarBucketId = import.meta.env.VITE_BUCKET_ID_AVATAR_COLLABORATOR!;

export type User = { id: string; name: string; email: string; joinedAt: string };
export type Profile = {
    $id: string;
    userId: string;
    firstName?: string;
    middleName?: string;
    lastName?: string;
    secondLastName?: string;
    email?: string;
    occupation?: string;
    occupationAbbreviation?: string;
    rolCorp?: string;
    colabCorpCode?: string;
    area?: string;
    departmentId?: string;
    joinDate?: string;
    leaveDate?: string;
    isActive?: boolean;
    bloodType?: string;
    birthday?: string;
    colabDniCode?: string;
    phone1?: string;
    phone2?: string;
    emergencyContact?: string;
    address?: string;
    idSupervisor?: string;
    bank?: string;
    bankAccountNumber?: string;
    bankAccountType?: string;
    keyFunctions?: string;
    keyFunction?: string[];
    archievements?: string;
    archievement?: string[];
    salary?: number;
};
export type CombinedUser = User & { profile: Profile | null; avatarUrl: string };

let cacheData: {
    users: CombinedUser[] | null;
    timestamp: number | null;
    avatarMap: Map<string, string> | null;
    isLoading: boolean;
} = {
    users: null,
    timestamp: null,
    avatarMap: null,
    isLoading: false,
};

const CACHE_DURATION = 10 * 60 * 1000;

async function getAvatarMap(): Promise<Map<string, string>> {
    if (cacheData.avatarMap && cacheData.timestamp && Date.now() - cacheData.timestamp < CACHE_DURATION) {
        return cacheData.avatarMap;
    }
    try {
        const allAvatars = await storage.listFiles(avatarBucketId);
        const avatarMap = new Map<string, string>();
        for (const file of allAvatars.files) {
            try {
                const url = storage.getFilePreview(avatarBucketId, file.$id, 100, 100);
                avatarMap.set(file.$id, url.toString());
            } catch {}
        }
        return avatarMap;
    } catch {
        return cacheData.avatarMap ?? new Map();
    }
}

export async function fetchUsersWithProfiles(forceRefresh = false): Promise<CombinedUser[]> {
    const isCacheValid = cacheData.users && cacheData.timestamp && Date.now() - cacheData.timestamp < CACHE_DURATION;
    if (isCacheValid && !forceRefresh && cacheData.users) return cacheData.users;

    if (cacheData.isLoading) {
        let attempts = 0;
        while (cacheData.isLoading && attempts < 30) {
            await new Promise(resolve => setTimeout(resolve, 500));
            attempts++;
        }
        if (cacheData.users && !cacheData.isLoading) return cacheData.users;
    }

    cacheData.isLoading = true;

    try {
        const [profilesResult, avatarMapResult] = await Promise.allSettled([
            databases.listDocuments(databaseId, collectionId, [Query.limit(500)]),
            getAvatarMap(),
        ]);

        const profiles: Profile[] = profilesResult.status === 'fulfilled'
            ? (profilesResult.value.documents as unknown as Profile[])
            : [];

        const avatarMap = avatarMapResult.status === 'fulfilled'
            ? avatarMapResult.value
            : new Map<string, string>();

        const combinedUsers: CombinedUser[] = profiles
            .filter(profile => !!profile.userId)
            .map(profile => {
                const nameParts = [profile.firstName, profile.middleName, profile.lastName, profile.secondLastName].filter(Boolean);
                return {
                    id: profile.userId,
                    name: nameParts.join(' ') || 'Sin nombre',
                    email: profile.email ?? '',
                    joinedAt: profile.joinDate ?? '',
                    profile,
                    avatarUrl: avatarMap.get(profile.userId) ?? '/images/perfil.png',
                };
            });

        cacheData = { users: combinedUsers, timestamp: Date.now(), avatarMap, isLoading: false };
        return combinedUsers;

    } catch {
        cacheData.isLoading = false;
        if (cacheData.users && cacheData.users.length > 0) return cacheData.users;
        return [];
    }
}

export function clearUsersCache() {
    cacheData = { users: null, timestamp: null, avatarMap: null, isLoading: false };
}

export async function refreshUsersData(): Promise<CombinedUser[]> {
    return fetchUsersWithProfiles(true);
}

export function getCacheStatus() {
    const age = cacheData.timestamp ? Date.now() - cacheData.timestamp : null;
    return {
        hasCache: !!cacheData.users,
        userCount: cacheData.users?.length ?? 0,
        ageMinutes: age ? Math.floor(age / 60000) : null,
        isValid: age ? age < CACHE_DURATION : false,
        isLoading: cacheData.isLoading,
    };
}
